import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Package, CheckCircle, Download, ArrowUUpLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';
import DatePicker from '@/components/DatePicker';
import InvoicesDialog from '@/components/InvoicesDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinanceDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [vendorOrders, setVendorOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewQuotationsOrder, setViewQuotationsOrder] = useState(null);
  const [approvedQuotations, setApprovedQuotations] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [poNotes, setPoNotes] = useState('');
  const [poFile, setPoFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesFilter, setInvoicesFilter] = useState('all'); // all | pending | paid
  const [invoicesPoTarget, setInvoicesPoTarget] = useState(null);

  const loadInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`, { headers: getAuthHeader() });
      setInvoices(res.data || []);
    } catch (error) {
      // silent — not fatal
      setInvoices([]);
    }
  };

  const loadData = async () => {
    try {
      const [voRes, poRes] = await Promise.all([
        axios.get(`${API}/vendor-orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/purchase-orders`, { headers: getAuthHeader() })
      ]);
      setVendorOrders(voRes.data);
      setPurchaseOrders(poRes.data);
      loadInvoices();
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleUploadPO = async () => {
    if (!selectedOrder || !poNumber.trim() || !poFile) {
      toast.error('PO number and PDF file are required');
      return;
    }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('po_number', poNumber.trim());
      if (poNotes.trim()) form.append('notes', poNotes.trim());
      form.append('file', poFile);

      const res = await axios.post(
        `${API}/vendor-orders/${selectedOrder.id}/upload-po`,
        form,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );

      // If expected delivery date is set, update it on the newly-created PO
      if (expectedDeliveryDate && res.data?.id) {
        await axios.put(`${API}/purchase-orders/${res.data.id}/update-delivery`, {
          expected_delivery_date: expectedDeliveryDate
        }, { headers: getAuthHeader() });
      }

      toast.success(`PO ${poNumber.trim()} uploaded — Ops/Lab notified to release it to the vendor.`);
      setSelectedOrder(null);
      setPoNumber('');
      setPoNotes('');
      setPoFile(null);
      setExpectedDeliveryDate('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PO');
    } finally {
      setUploadBusy(false);
    }
  };

  const loadApprovedQuotations = async (order) => {
    try {
      const res = await axios.get(`${API}/vendor-orders/${order.id}/approved-quotations`, {
        headers: getAuthHeader(),
      });
      setApprovedQuotations(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load quotations');
      setApprovedQuotations([]);
    }
  };

  const handleDownloadQuotationPdf = async (quotationId) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation_${quotationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('This quotation has no PDF attached');
    }
  };

  const handleDownloadCombinedPdf = async () => {
    if (!viewQuotationsOrder) return;
    try {
      const response = await axios.get(
        `${API}/vendor-orders/${viewQuotationsOrder.id}/combined-quotations-pdf`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const vendorSlug = (viewQuotationsOrder.vendor_name || 'vendor').replace(/[^a-z0-9]/gi, '_');
      link.setAttribute('download', `quotations_${vendorSlug}_${viewQuotationsOrder.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download combined PDF');
    }
  };

  useEffect(() => {
    if (viewQuotationsOrder) loadApprovedQuotations(viewQuotationsOrder);
    else setApprovedQuotations([]);
  }, [viewQuotationsOrder]);

  const handleExportPO = async (poId, poNumber) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${poId}/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${poNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Purchase order exported successfully');
    } catch (error) {
      toast.error('Failed to export PO');
    }
  };

  const readyOrders = vendorOrders.filter(o => o.status === 'ready_for_po');
  const processedOrders = vendorOrders.filter(o => o.status === 'po_created');
  const accumulatingOrders = vendorOrders.filter(o => o.status === 'accumulating');

  const [poKw, setPoKw] = useState('');
  const [poStatus, setPoStatus] = useState('all');
  const [poStart, setPoStart] = useState('');
  const [poEnd, setPoEnd] = useState('');

  const filteredPOs = useMemo(
    () => filterRecords(purchaseOrders, {
      keyword: poKw, statusValue: poStatus,
      startDate: poStart, endDate: poEnd,
      fields: ['po_number', 'vendor_name'],
    }),
    [purchaseOrders, poKw, poStatus, poStart, poEnd]
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
            Finance Dashboard
          </h1>
          <p className="mt-2 text-text-secondary">Manage purchase orders and vendor payments</p>
        </div>

        <Tabs defaultValue="ready" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="ready" data-testid="ready-tab">Ready for PO ({readyOrders.length})</TabsTrigger>
            <TabsTrigger value="created" data-testid="created-tab">
              Created POs ({purchaseOrders.length})
              {purchaseOrders.filter(p => p.status === 'pending_release').length > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white"
                  title={`${purchaseOrders.filter(p => p.status === 'pending_release').length} PO(s) still awaiting release to vendor`}
                  data-testid="finance-pending-release-badge"
                >
                  {purchaseOrders.filter(p => p.status === 'pending_release').length} awaiting release
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="invoices-tab">
              Invoices ({invoices.length})
              {invoices.filter(i => i.status === 'pending').length > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white"
                  title={`${invoices.filter(i => i.status === 'pending').length} invoice(s) awaiting payment`}
                  data-testid="finance-pending-invoices-badge"
                >
                  {invoices.filter(i => i.status === 'pending').length} pending
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="processed" data-testid="processed-tab">Processed ({processedOrders.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="ready" className="mt-6">
            <div className="grid gap-4">
              {readyOrders.map((order) => (
                <div key={order.id} className="border border-border-default rounded p-6" data-testid={`ready-order-${order.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-accent-brand-subtle flex items-center justify-center">
                        <Package size={24} className="text-accent-brand" weight="duotone" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-text-primary">{order.vendor_name}</h3>
                        <p className="text-text-secondary">{order.requisition_ids.length} items</p>
                        <p className="text-2xl font-bold text-accent-brand mt-2">₹{order.total_amount.toFixed(2)}</p>
                        <p className="text-sm text-text-tertiary mt-1">
                          Deadline: {new Date(order.order_deadline).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setViewQuotationsOrder(order)}
                        data-testid={`view-quotations-btn-${order.id}`}
                        title="View & download the approved vendor's quotations for this order"
                      >
                        <Download size={18} className="mr-2" weight="duotone" />
                        Quotations
                      </Button>
                      <Button
                        className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`upload-po-btn-${order.id}`}
                      >
                        <Receipt size={20} className="mr-2" weight="duotone" />
                        Upload PO
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {readyOrders.length === 0 && (
                <div className="text-center py-12 bg-surface rounded space-y-3" data-testid="finance-empty-ready">
                  <p className="text-text-secondary">No vendor orders ready for PO creation.</p>
                  {accumulatingOrders.length > 0 && (
                    <div className="max-w-xl mx-auto text-sm text-text-tertiary space-y-2">
                      <p>
                        <strong className="text-text-primary">{accumulatingOrders.length}</strong> vendor
                        order{accumulatingOrders.length !== 1 ? 's' : ''} still in the 14-day accumulation window
                        (total value: <strong className="text-text-primary">₹{accumulatingOrders.reduce((s, o) => s + (o.total_amount || 0), 0).toFixed(2)}</strong>).
                      </p>
                      <p>
                        These will auto-move to &ldquo;Ready for PO&rdquo; once their deadline passes, or the Operations
                        Manager can finalize them early from the Operations dashboard.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="created" className="mt-6 space-y-3">
            <SearchFilterBar
              keyword={poKw} onKeywordChange={setPoKw}
              statusValue={poStatus} onStatusChange={setPoStatus}
              statusOptions={[
                { value: 'pending_release', label: 'Pending Release' },
                { value: 'released', label: 'Released' },
                { value: 'completed', label: 'Completed' },
              ]}
              startDate={poStart} endDate={poEnd}
              onStartDateChange={setPoStart} onEndDateChange={setPoEnd}
              onClear={() => { setPoKw(''); setPoStatus('all'); setPoStart(''); setPoEnd(''); }}
              placeholder="Search PO # or vendor…"
              testIdPrefix="finance-po-filter"
            />
            <div className="bg-white border border-border-default rounded">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border-default">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">PO Number</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Items</th>
                      <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Amount</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Created</th>
                      <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPOs.length === 0 && (
                      <tr><td colSpan="7" className="p-8 text-center text-text-tertiary">No purchase orders match your filters.</td></tr>
                    )}
                    {filteredPOs.map((po) => (
                      <tr key={po.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`po-row-${po.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                              <Receipt size={20} className="text-accent-brand" weight="duotone" />
                            </div>
                            <span className="font-mono font-semibold text-text-primary">{po.po_number}</span>
                          </div>
                        </td>
                        <td className="p-4 text-text-secondary">{po.vendor_name}</td>
                        <td className="p-4 text-text-secondary">{po.requisition_ids.length} items</td>
                        <td className="p-4 text-right font-semibold text-text-primary">₹{po.total_amount.toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`status-badge ${
                            po.status === 'pending_release' ? 'status-pending' : 
                            po.status === 'released' ? 'status-approved' : 'status-info'
                          }`}>
                            {po.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-4 text-text-secondary">
                          {new Date(po.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end items-center">
                            {po.actual_delivery_date && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold" data-testid={`po-delivered-indicator-${po.id}`}>
                                <CheckCircle size={14} weight="fill" />
                                Delivered {new Date(po.actual_delivery_date).toLocaleDateString()}
                              </span>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleExportPO(po.id, po.po_number)}
                              data-testid={`export-po-${po.id}`}
                            >
                              <Download size={16} className="mr-1" weight="bold" />
                              PDF
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-text-secondary">
                Invoices uploaded by Lab / Operations against released POs. Download the PDF, feed it into your accounting system, then mark it paid.
              </p>
              <Select value={invoicesFilter} onValueChange={setInvoicesFilter}>
                <SelectTrigger className="w-40" data-testid="invoices-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white border border-border-default rounded">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border-default">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Invoice #</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">PO #</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
                      <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Amount</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Uploaded by</th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
                      <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.filter(i => invoicesFilter === 'all' || i.status === invoicesFilter).length === 0 && (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-text-tertiary" data-testid="invoices-empty-row">
                          {invoicesFilter === 'all'
                            ? 'No invoices uploaded yet. Lab and Operations Managers can upload invoices against released POs.'
                            : `No ${invoicesFilter} invoices.`}
                        </td>
                      </tr>
                    )}
                    {invoices
                      .filter(i => invoicesFilter === 'all' || i.status === invoicesFilter)
                      .map((inv) => (
                        <tr key={inv.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`finance-invoice-row-${inv.id}`}>
                          <td className="p-4 font-mono font-semibold text-text-primary">{inv.invoice_number}</td>
                          <td className="p-4 font-mono text-text-secondary">{inv.po_number}</td>
                          <td className="p-4 text-text-secondary">{inv.vendor_name}</td>
                          <td className="p-4 text-right font-semibold">₹{Number(inv.amount || 0).toFixed(2)}</td>
                          <td className="p-4 text-text-secondary">
                            {inv.uploaded_by_name}
                            <div className="text-xs text-text-tertiary">
                              {(inv.uploaded_by_role || '').replace(/_/g, ' ')} · {new Date(inv.uploaded_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="p-4">
                            <span
                              className={`status-badge ${inv.status === 'paid' ? 'status-approved' : 'status-pending'}`}
                              data-testid={`finance-invoice-status-${inv.id}`}
                            >
                              {inv.status}
                            </span>
                            {inv.status === 'paid' && inv.paid_by_name && (
                              <div className="text-xs text-text-tertiary mt-1">by {inv.paid_by_name}</div>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const response = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
                                      headers: getAuthHeader(),
                                      responseType: 'blob',
                                    });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', inv.invoice_file_name || `Invoice_${inv.invoice_number}.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                  } catch (e) {
                                    toast.error('Failed to download invoice PDF');
                                  }
                                }}
                                data-testid={`finance-download-invoice-${inv.id}`}
                              >
                                <Download size={14} className="mr-1" weight="duotone" />
                                PDF
                              </Button>
                              {inv.status === 'paid' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/invoices/${inv.id}/mark-paid`, { paid: false }, { headers: getAuthHeader() });
                                      toast.success(`Invoice ${inv.invoice_number} reverted to pending`);
                                      loadInvoices();
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Failed to update');
                                    }
                                  }}
                                  data-testid={`finance-unmark-paid-${inv.id}`}
                                >
                                  <ArrowUUpLeft size={14} className="mr-1" />
                                  Undo
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/invoices/${inv.id}/mark-paid`, { paid: true }, { headers: getAuthHeader() });
                                      toast.success(`Invoice ${inv.invoice_number} marked as PAID`);
                                      loadInvoices();
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Failed to update');
                                    }
                                  }}
                                  data-testid={`finance-mark-paid-${inv.id}`}
                                >
                                  <CheckCircle size={14} className="mr-1" weight="fill" />
                                  Mark Paid
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const po = purchaseOrders.find(p => p.id === inv.po_id);
                                  if (po) setInvoicesPoTarget(po);
                                }}
                                data-testid={`finance-view-po-invoices-${inv.id}`}
                                title="View all invoices for this PO"
                              >
                                <Receipt size={14} className="mr-1" weight="duotone" />
                                All for PO
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="processed" className="mt-6">
            <div className="grid gap-4">
              {processedOrders.map((order) => (
                <div key={order.id} className="border border-border-default rounded p-6" data-testid={`processed-order-${order.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-status-success-bg flex items-center justify-center">
                      <CheckCircle size={24} className="text-status-success" weight="duotone" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-text-primary">{order.vendor_name}</h3>
                          <p className="text-text-secondary">{order.requisition_ids.length} items • ₹{order.total_amount.toFixed(2)}</p>
                        </div>
                        <span className="status-badge status-approved">PO Created</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Upload PO Dialog */}
        {selectedOrder && (
          <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open && !uploadBusy) { setSelectedOrder(null); setPoNumber(''); setPoNotes(''); setPoFile(null); setExpectedDeliveryDate(''); }}}>
            <DialogContent className="sm:max-w-lg" data-testid="upload-po-dialog">
              <DialogHeader>
                <DialogTitle>Upload Purchase Order</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="p-4 bg-surface rounded">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-text-tertiary">Vendor</p>
                      <p className="font-semibold text-text-primary">{selectedOrder.vendor_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Total Amount</p>
                      <p className="font-bold text-accent-brand text-xl">₹{selectedOrder.total_amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Items</p>
                      <p className="text-text-primary">{selectedOrder.requisition_ids.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Deadline</p>
                      <p className="text-text-primary">{new Date(selectedOrder.order_deadline).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-text-tertiary">
                  Create the PO in your accounting software, then upload the signed PDF below. Once uploaded, Ops
                  and Lab will be notified to release it to the vendor.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="po_number">PO Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="po_number"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    placeholder="From your accounting software (e.g., PO-2026-001)"
                    required
                    data-testid="po-number-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="po_file">Signed PO PDF <span className="text-red-500">*</span></Label>
                  <input
                    id="po_file"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setPoFile(e.target.files?.[0] || null)}
                    data-testid="po-file-input"
                    className="block w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-accent-brand-subtle file:text-accent-brand hover:file:bg-accent-brand-subtle"
                  />
                  {poFile && <p className="text-xs text-text-tertiary">Selected: <span className="font-mono">{poFile.name}</span> ({(poFile.size / 1024).toFixed(0)} KB)</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="po_notes">Notes (optional)</Label>
                  <textarea
                    id="po_notes"
                    value={poNotes}
                    onChange={(e) => setPoNotes(e.target.value)}
                    placeholder="Any special instructions for Ops/Lab before releasing to vendor…"
                    className="w-full min-h-[60px] rounded border border-border-default px-3 py-2 text-sm"
                    data-testid="po-notes-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expected_delivery">Expected Delivery Date</Label>
                  <DatePicker
                    value={expectedDeliveryDate}
                    onChange={setExpectedDeliveryDate}
                    placeholder="Select expected date"
                    testId="expected-delivery-input"
                    className="w-full"
                  />
                </div>

                <Button
                  className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
                  disabled={!poNumber.trim() || !poFile || uploadBusy}
                  onClick={handleUploadPO}
                  data-testid="submit-po-button"
                >
                  {uploadBusy ? 'Uploading…' : 'Upload PO'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* View Approved Quotations Dialog */}
        {viewQuotationsOrder && (
          <Dialog open={!!viewQuotationsOrder} onOpenChange={(open) => !open && setViewQuotationsOrder(null)}>
            <DialogContent className="sm:max-w-2xl" data-testid="view-quotations-dialog">
              <DialogHeader>
                <DialogTitle>Approved Quotations — {viewQuotationsOrder.vendor_name}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-text-tertiary">
                  Download the source documents you need to draft the PO in your accounting software.
                </p>
                {approvedQuotations.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadCombinedPdf}
                    data-testid="download-combined-quotations-btn"
                    title="Stitch every quotation for this vendor order into a single PDF"
                  >
                    <Download size={14} className="mr-1" weight="duotone" />
                    Download All ({approvedQuotations.length}) as one PDF
                  </Button>
                )}
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {approvedQuotations.length === 0 ? (
                  <div className="text-center py-8 text-text-tertiary text-sm">No quotations found for this vendor order.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-surface border-b border-border-default sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Reagent</th>
                        <th className="p-2 text-left">Cat #</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2 text-right">Unit Price</th>
                        <th className="p-2 text-right">Line Total</th>
                        <th className="p-2 text-right">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedQuotations.map((q) => (
                        <tr key={q.quotation_id} className="border-b border-border-default" data-testid={`approved-quot-${q.quotation_id}`}>
                          <td className="p-2 font-medium text-text-primary">{q.reagent_name}</td>
                          <td className="p-2 font-mono text-xs">{q.catalogue_number}</td>
                          <td className="p-2 text-right">{q.quantity}</td>
                          <td className="p-2 text-right">₹{Number(q.price || 0).toFixed(2)}</td>
                          <td className="p-2 text-right font-semibold">₹{(Number(q.price || 0) * Number(q.quantity || 1)).toFixed(2)}</td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadQuotationPdf(q.quotation_id)}
                              data-testid={`download-quotation-pdf-${q.quotation_id}`}
                              title={q.has_uploaded_pdf ? "Download vendor's original quotation PDF" : "Download auto-generated summary (no source PDF was uploaded for this quotation)"}
                            >
                              <Download size={14} className="mr-1" weight="duotone" />
                              {q.has_uploaded_pdf ? 'PDF' : 'Summary'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Invoices Dialog */}
        <InvoicesDialog
          po={invoicesPoTarget}
          open={!!invoicesPoTarget}
          onClose={() => setInvoicesPoTarget(null)}
          onChanged={loadInvoices}
        />

      </div>
    </DashboardLayout>
  );
};

export default FinanceDashboard;
